import { RenderActionButtonProps } from "../../../../types";
import {
  PrimaryButton,
  IconSave,
  IconAdd,
  SecondaryButton,
  IconRefresh,
} from "@cdkglobal/radial";
import { useUIStateContext } from "../../../../contexts/UIStateContext";
import {
  evaluateSchemaContext,
  getUpdatedRowDataIfTabsExist,
  isNewRecord,
} from "../../helpers";
import { getIsRecordChanged } from "../../validations";
import { FEATURE_FLAG_KEYS } from "../../constants";
import { useFeatureFlag } from "@harnessio/ff-react-client-sdk";
import { useUserContext } from "../../../../contexts/UserContext";

export default function RenderActionButton({
  propertyKey,
  isDisabled,
  requiresClear,
  rowDataReducerState,
  dispatch,
}: Readonly<RenderActionButtonProps>): JSX.Element | null {
  const {
    landingPageToast,
    recordPageToast,
    selectedDashboardTabSectionKey,
    isRecordOpen,
    incrementSaveRequestId,
    selectedTabSectionKey,
    activeTabIndex,
  } = useUIStateContext();

  const { permissions } = useUserContext();

  const { setupMode } = evaluateSchemaContext({
    schema: rowDataReducerState.schema,
    activeTabIndex,
    selectedDashboardTabSectionKey,
    isRecordOpen,
  });

  const isSaveOnChangeForExistingRecordsFlagEnabled: boolean = useFeatureFlag(
    FEATURE_FLAG_KEYS.ENABLE_SAVE_ON_CHANGE_FOR_EXISTING_RECORDS,
    false,
  );

  const isRecordChanged = getIsRecordChanged({
    selectedTabSectionKey,
    selectedDashboardTabSectionKey,
    rowDataReducerState,
  });

  const isSaveDisabled =
    isSaveOnChangeForExistingRecordsFlagEnabled &&
    !isNewRecord(rowDataReducerState.rowData) &&
    !isRecordChanged;

  const isInAction = Boolean(
    (recordPageToast.variant === "neutral" && recordPageToast.message) ||
    (landingPageToast.variant === "neutral" && landingPageToast.message),
  );
  const restricted =
    isDisabled || isInAction || !permissions.setupPermission.hasManage;

  const handleSaveClick = () => {
    incrementSaveRequestId();
  };

  const handleAddClick = () => {
    // TODO: Need to call a dispath to keep the current updated active rowData
  };

  const handleClearClick = () => {
    const { resetState } = rowDataReducerState;
    const preservedNewEntryRowData = resetState?.newEntryRowData ?? {};

    const updatedResetNewEntryRowData = getUpdatedRowDataIfTabsExist(
      preservedNewEntryRowData,
      selectedDashboardTabSectionKey,
    );
    dispatch({
      type: "UPDATE_ROW_DATA_REDUCER_STATE",
      payload: {
        // schema: resetState?.schema, // We are not updating the schema here as we want to keep the current schema intact.
        rowData: updatedResetNewEntryRowData,
        rowDataErrors: {},
        deletedObjectsList: {},
        isReadOnly: !permissions.setupPermission.hasManage,
      },
    });
  };

  if (setupMode !== "NORMAL_SETUP" && isRecordOpen) {
    return null;
  }

  if (!isRecordOpen) {
    return (
      <>
        {requiresClear && (
          <SecondaryButton
            dataTestId={`${propertyKey}-clear-button`}
            icon={<IconRefresh />}
            iconSize={24}
            onClick={handleClearClick}
            size="large"
            text={"Clear"}
            style={{ margin: "0 0.5rem 0 0" }}
            isDisabled={isInAction}
          />
        )}

        <PrimaryButton
          dataTestId={`${propertyKey}-save-button`}
          icon={<IconSave />}
          iconColor="primary"
          iconSize={24}
          onClick={handleSaveClick}
          size="large"
          text={"Save"}
          isDisabled={restricted || isSaveDisabled}
        />
      </>
    );
  }

  return (
    <PrimaryButton
      dataTestId={`${propertyKey}-add-button`}
      icon={<IconAdd />}
      iconColor="primary"
      iconSize={24}
      onClick={handleAddClick}
      size="large"
      text={"Add"}
      isDisabled={restricted}
    />
  );
}
